<template>
    <div class="mt-4" v-if="isVisible">
        <b-alert show variant="info" class="mb-3">
            <div class="d-flex align-items-center">
                <i class="fas fa-users mr-2"></i>
                <strong>Несколько исполнителей по строкам материалов</strong>
            </div>
            <div class="small mt-1">
                Назначающий может распределить материалы между несколькими исполнителями.
                Каждый исполнитель видит все материалы, но отгружает только свои строки.
            </div>
        </b-alert>

        <div class="multi-executor-panel p-3 border rounded bg-light">
            <div class="d-flex flex-wrap align-items-center mb-3">
                <b-form-checkbox
                    v-model="multiModeEnabled"
                    :disabled="!canEnableMultiExecutor || assigningExecutor"
                    switch
                >
                    <strong>Несколько исполнителей</strong>
                </b-form-checkbox>
                <small class="text-muted ml-3">
                    <template v-if="isAwaitingExecutor">После сохранения заявка перейдёт в работу</template>
                    <template v-else>Заявка останется в работе</template>
                </small>
            </div>

            <div v-if="multiModeEnabled">
                <div class="row mb-3 align-items-end">
                    <div class="col-md-6">
                        <label class="small text-muted mb-1">Применить ко всем пустым строкам:</label>
                        <v-select
                            v-model="bulkExecutorId"
                            :options="multiExecutorOptions"
                            label="text"
                            :reduce="o => o.value"
                            :disabled="assigningExecutor"
                            placeholder="Выберите исполнителя для bulk-применения"
                            :clearable="true"
                        />
                        <EmployeeSelectionPreview :user-id="bulkExecutorId" :options="multiExecutorOptions" />
                    </div>
                    <div class="col-md-6 d-flex">
                        <b-button
                            variant="outline-secondary"
                            size="sm"
                            class="mr-2"
                            :disabled="!bulkExecutorId || assigningExecutor"
                            @click="applyBulkToEmpty"
                        >
                            <i class="fas fa-fill-drip"></i> Применить ко всем пустым
                        </b-button>
                        <b-button
                            variant="outline-secondary"
                            size="sm"
                            :disabled="!bulkExecutorId || assigningExecutor"
                            @click="applyBulkToAll"
                        >
                            <i class="fas fa-paint-roller"></i> Применить ко всем
                        </b-button>
                    </div>
                </div>

                <b-table
                    striped
                    hover
                    small
                    :items="rows"
                    :fields="tableFields"
                    class="multi-executor-table"
                    thead-class="text-nowrap"
                    stacked="md"
                >
                    <template #cell(item_name)="row">
                        <div class="font-weight-bold">{{ row.item.item_name }}</div>
                        <small class="text-muted">#{{ row.item.id }}</small>
                    </template>
                    <template #cell(quantity)="row">
                        <span>{{ row.item.quantity }} {{ row.item.unit || '' }}</span>
                    </template>
                    <template #cell(executor)="row">
                        <!-- Обёртка: Bootstrap 4 border-danger гарантированно виден; vue-select не всегда пробрасывает class на корень -->
                        <div
                            class="multi-exec-select-wrap w-100"
                            :class="{ 'multi-exec-select-wrap--invalid': isExecutorUnset(row.item.id) }"
                        >
                            <v-select
                                v-model="assignmentMap[row.item.id]"
                                :options="multiExecutorOptions"
                                label="text"
                                :reduce="o => o.value"
                                :disabled="assigningExecutor"
                                :clearable="false"
                                placeholder="Выберите исполнителя"
                                append-to-body
                            />
                            <EmployeeSelectionPreview :user-id="assignmentMap[row.item.id]" :options="multiExecutorOptions" />
                        </div>
                    </template>
                </b-table>

                <div class="d-flex flex-wrap align-items-center justify-content-between mt-3">
                    <div class="text-muted small">
                        <span v-if="emptyCount > 0" class="text-danger">
                            <i class="fas fa-exclamation-triangle"></i>
                            Не назначен исполнитель для {{ emptyCount }} строк(и)
                        </span>
                        <span v-else class="text-success">
                            <i class="fas fa-check-circle"></i>
                            Все строки назначены ({{ rows.length }})
                        </span>
                    </div>
                    <b-button
                        variant="primary"
                        :disabled="!isFormValid || assigningExecutor"
                        @click="onSave"
                    >
                        <template v-if="assigningExecutor">
                            <b-spinner small class="mr-1"></b-spinner>
                            Сохранение...
                        </template>
                        <template v-else>
                            <i class="fas fa-save"></i> Сохранить назначения
                        </template>
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EmployeeSelectionPreview from '@/components/EmployeeSelectionPreview.vue';
import { mapState, mapGetters, mapActions } from 'vuex';

/**
 * Компонент включения режима «несколько исполнителей» и распределения материалов.
 * Виден назначающему в awaiting_executor или одиночном in_progress для типов с
 * executor_assignment_manual=true.
 */
export default {
    components: {EmployeeSelectionPreview},
    name: 'MultiExecutorAssign',
    data() {
        return {
            multiModeEnabled: false,
            bulkExecutorId: null,
            assignmentMap: {}
        };
    },
    computed: {
        ...mapState('requestCard', ['assigningExecutor']),
        ...mapGetters('requestCard', [
            'isMultiExecutor',
            'canEnableMultiExecutor',
            'executorOptions',
            'executors',
            'materialItems'
        ]),
        request() {
            return this.$store.state.requestCard.request;
        },
        isAwaitingExecutor() {
            return this.request && this.request.status === 'awaiting_executor';
        },
        isInProgress() {
            return this.request && this.request.status === 'in_progress';
        },
        activeExecutorId() {
            const participant = (this.executors || []).find(executor => executor.status === 'active');
            const userId = participant && Number(participant.user_id);
            return userId || null;
        },
        multiExecutorOptions() {
            const options = (this.executorOptions || []).map(option => ({ ...option }));
            const knownIds = new Set(options.map(option => Number(option.value)));

            for (const executor of (this.executors || [])) {
                const userId = Number(executor.user_id);
                if (!userId || knownIds.has(userId) || executor.status !== 'active') continue;

                const firstName = executor.first_name ?? executor.User?.first_name ?? '';
                const lastName = executor.last_name ?? executor.User?.last_name ?? '';
                const name = `${firstName} ${lastName}`.trim();
                options.unshift({
                    value: userId,
                    text: name || `Пользователь #${userId}`,
                    priority: executor.priority
                });
                knownIds.add(userId);
            }

            return options;
        },
        /**
         * После включения панель скрывается: дальнейшее переназначение выполняется в таблице материалов.
         */
        isVisible() {
            return this.canEnableMultiExecutor && !this.isMultiExecutor;
        },
        rows() {
            return (this.materialItems || []).map(i => ({
                id: i.id,
                item_name: i.item_name,
                quantity: i.quantity,
                unit: i.unit,
                executor_id: i.executor_id || (i.executor && i.executor.user_id) || null
            }));
        },
        tableFields() {
            return [
                { key: 'item_name', label: 'Материал' },
                { key: 'quantity', label: 'Запрошено', thStyle: { width: '160px' } },
                { key: 'executor', label: 'Исполнитель', thStyle: { width: '320px' } }
            ];
        },
        emptyCount() {
            return this.rows.reduce((acc, row) => {
                const v = this.assignmentMap[row.id];
                return acc + (v ? 0 : 1);
            }, 0);
        },
        isFormValid() {
            return this.rows.length > 0 && this.emptyCount === 0;
        }
    },
    watch: {
        materialItems: {
            immediate: true,
            handler(items) {
                if (!Array.isArray(items)) return;
                const next = { ...this.assignmentMap };
                const defaultExecutorId = this.isInProgress && !this.isMultiExecutor
                    ? this.activeExecutorId
                    : null;
                for (const i of items) {
                    if (next[i.id] == null) {
                        next[i.id] = i.executor_id ||
                            (i.executor && i.executor.user_id) ||
                            defaultExecutorId ||
                            null;
                    }
                }
                // Удалим лишние ключи, если строки удалили
                const validIds = new Set(items.map(i => i.id));
                Object.keys(next).forEach(k => {
                    if (!validIds.has(parseInt(k, 10))) delete next[k];
                });
                this.assignmentMap = next;
            }
        },
        isMultiExecutor: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.multiModeEnabled = true;
                }
            }
        }
    },
    methods: {
        ...mapActions('requestCard', ['requestCard_assignMultiExecutors']),

        applyBulkToEmpty() {
            if (!this.bulkExecutorId) return;
            const next = { ...this.assignmentMap };
            this.rows.forEach(row => {
                if (!next[row.id]) {
                    next[row.id] = this.bulkExecutorId;
                }
            });
            this.assignmentMap = next;
        },

        applyBulkToAll() {
            if (!this.bulkExecutorId) return;
            const next = { ...this.assignmentMap };
            this.rows.forEach(row => {
                next[row.id] = this.bulkExecutorId;
            });
            this.assignmentMap = next;
        },

        /** Нет выбранного исполнителя — для подсветки .is-invalid (Bootstrap 4) */
        isExecutorUnset(itemId) {
            const v = this.assignmentMap[itemId];
            return v === null || v === undefined || v === '';
        },

        async onSave() {
            if (!this.isFormValid) {
                this.$bvToast && this.$bvToast.toast(
                    'Назначьте исполнителя для каждой строки',
                    { title: 'Невозможно сохранить', variant: 'warning', solid: true }
                );
                return;
            }

            const statusMessage = this.isAwaitingExecutor
                ? 'Назначения будут применены, заявка перейдёт в статус «В работе».'
                : 'Назначения будут применены, заявка останется в статусе «В работе».';
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Включить режим «несколько исполнителей»? ${statusMessage}`,
                { contentClass: 'argus-glass-dialog',
                    title: 'Распределение материалов',
                    size: 'md',
                    okVariant: 'primary',
                    okTitle: 'Да, сохранить',
                    cancelTitle: 'Отмена',
                    centered: true
                }
            );
            if (!confirmed) return;

            const assignments = this.rows.map(row => ({
                item_id: row.id,
                user_id: this.assignmentMap[row.id]
            }));

            try {
                await this.requestCard_assignMultiExecutors({ assignments });
                this.$bvToast && this.$bvToast.toast(
                    'Материалы распределены между исполнителями',
                    { title: 'Готово', variant: 'success', solid: true }
                );
            } catch (error) {
                const errorMsg = error?.response?.data?.message || 'Не удалось сохранить назначения';
                this.$bvToast && this.$bvToast.toast(errorMsg, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            }
        }
    }
};
</script>

<style scoped>
.multi-executor-panel {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
}
.multi-executor-table >>> td {
    vertical-align: middle;
}

/* Нет исполнителя: рамка как у .form-control.is-invalid (Bootstrap 4) */
.multi-exec-select-wrap--invalid {
    border: 1px solid #dc3545;
    border-radius: 0.25rem;
    background-color: rgba(220, 53, 69, 0.04);
}
.multi-exec-select-wrap--invalid >>> .vs__dropdown-toggle {
    border-color: transparent !important;
    background-color: transparent;
}
.multi-exec-select-wrap--invalid >>> .vs__dropdown-toggle:focus {
    box-shadow: none;
}

@media (max-width: 767.98px) {
    .multi-executor-table >>> tbody tr {
        margin-bottom: .75rem;
        border: 1px solid #dee2e6;
        border-radius: .35rem;
        background: #fff;
        box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
        overflow: hidden;
    }

    .multi-executor-table >>> tbody td {
        padding: .6rem .75rem;
        border-top: 1px solid #eef0f2;
    }

    .multi-executor-table >>> tbody td:first-child {
        border-top: 0;
    }

    .multi-executor-table >>> tbody td::before {
        color: #6c757d;
        font-size: .78rem;
        font-weight: 700;
        text-align: left;
    }

    .multi-executor-table >>> .vs__dropdown-toggle {
        min-height: 36px;
    }
}
</style>
